import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepSection } from '@/components/step-section';
import { ApiError } from '@/lib/api';
import { use_draft, use_start_onboarding } from '@/features/onboarding/hooks';
import { ALL_MODULE_TYPES, type VendorModuleType } from '@/features/onboarding/types';

export function StartPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const start_m = use_start_onboarding();
  const [selected, set_selected] = useState<Set<VendorModuleType>>(new Set());
  const [error, set_error] = useState<string | null>(null);

  useEffect(() => {
    if (draft_q.data) {
      set_selected(new Set(draft_q.data.module_types));
    }
  }, [draft_q.data]);

  /** Only a submitted application freezes the module list; drafts stay editable. */
  const locked = !!draft_q.data?.submitted_at;

  function toggle(m: VendorModuleType) {
    // Functional update: reading `selected` from the render closure loses
    // every pick but the last when several toggles land in one React batch.
    set_selected((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });
  }

  async function handle_continue() {
    set_error(null);
    if (selected.size === 0) {
      set_error('Pick at least one module to continue');
      return;
    }
    try {
      // Safe to send every time: the backend creates the draft on first call and
      // re-points an unsubmitted one afterwards, so edits actually persist.
      if (!locked) {
        await start_m.mutateAsync({ module_types: Array.from(selected) });
      }
      navigate('/onboarding/basic-info');
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'Could not start onboarding');
    }
  }

  return (
    <StepSection
      step_key="start"
      title="Select your modules"
      description="Pick the vendor types you offer. You can register for multiple modules in a single account."
      error={error}
      footer={
        <Button
          variant="gradient"
          onClick={handle_continue}
          disabled={start_m.isPending || draft_q.isLoading}
          data-testid="start-continue"
        >
          {start_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {draft_q.data ? 'Continue' : 'Start onboarding'}
        </Button>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {ALL_MODULE_TYPES.map((m) => {
          const checked = selected.has(m);
          return (
            <button
              key={m}
              type="button"
              data-testid={`module-${m}`}
              data-checked={checked}
              onClick={() => !locked && toggle(m)}
              disabled={locked}
              className={
                'flex items-center justify-between rounded-lg border-2 px-4 py-3 text-left transition-all ' +
                (checked
                  ? 'border-primary bg-primary/5 text-foreground'
                  : 'border-border bg-card text-muted-foreground hover:border-primary/50') +
                (locked ? ' cursor-not-allowed opacity-70' : '')
              }
            >
              <span className="font-semibold">{m}</span>
              <span
                className={
                  'h-5 w-5 rounded-full border-2 ' +
                  (checked ? 'border-primary bg-primary' : 'border-border')
                }
              />
            </button>
          );
        })}
      </div>
      {locked && (
        <p className="text-xs text-muted-foreground">
          Your application has been submitted, so modules can no longer be changed. Contact support
          if you need to add or remove one.
        </p>
      )}
    </StepSection>
  );
}
